import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import useUiContext from '@/custolink-hooks/useUiContext'
import useAssetViewTranslator from '@/custolink-views/asset_frontend/useAssetViewTranslator'
import { getVisitedAssets } from '@/custolink-views/asset_frontend/visited_assets'
import {
  PublicAssetFrontendApi,
  PublicAssetFrontendApi$AssetComponentDto,
  PublicAssetFrontendApi$AssetComponentDtoKey,
  PublicAssetFrontendApi$AssetComponents,
} from '@/custolink-transpiled_by_server/bc.asset_interaction.api_methods'
import { normaliseHex } from './theme'

export type VisitedEntry = ReturnType<typeof getVisitedAssets>[number]

export type HomeModel = ReturnType<typeof useHomeModel>

/**
 * Everything the home page does, with no opinion about how it looks: the same
 * component query, the same translation pass, the same drawer state and the
 * same "voice call opens straight away, everything else asks which asset"
 * routing the existing page uses. The preview drives only its own composition
 * from this.
 */
export function useHomeModel() {
  const uic = useUiContext()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { translateNames } = useAssetViewTranslator()
  const lf = uic.organisationContext.lookAndFeel

  const q = useQuery({
    queryFn: () => PublicAssetFrontendApi.getComponents(uic._orgId),
    queryKey: ['PublicAssetFrontendApi.getComponents', uic._orgId],
  })

  const components: PublicAssetFrontendApi$AssetComponents | null = useMemo(() => {
    if (q.data?.components == null) return null
    return {
      bottomSection: translateNames(q.data.components.bottomSection),
      topSection: translateNames(q.data.components.topSection),
      middleSection: translateNames(q.data.components.middleSection),
    }
  }, [q.data?.components, translateNames])

  const recent: VisitedEntry[] = useMemo(() => getVisitedAssets(uic._orgId), [uic._orgId])

  const [accountOpen, setAccountOpen] = useState(false)
  const [scanOpen, setScanOpen] = useState(false)
  const [selectAssetOpen, setSelectAssetOpen] = useState(false)
  const [voiceComponent, setVoiceComponent] =
    useState<PublicAssetFrontendApi$AssetComponentDto | null>(null)
  const [firstTimeOpen, setFirstTimeOpen] = useState(
    () => localStorage.getItem('firsttime_dialog_shown') !== 'true'
  )

  function closePanels() {
    setAccountOpen(false)
    setScanOpen(false)
    setSelectAssetOpen(false)
    setVoiceComponent(null)
  }

  const isVoice = (c: PublicAssetFrontendApi$AssetComponentDto) =>
    c.key === PublicAssetFrontendApi$AssetComponentDtoKey.VoiceCall

  const searchPlaceholder =
    lf.i18nAssetFrontendSnoSearchbarPlaceholder != null
      ? t(lf.i18nAssetFrontendSnoSearchbarPlaceholder)
      : lf.assetFrontendSnoSearchbarPlaceholder

  return {
    logoUrl: lf.logoUrl,
    accent: normaliseHex(lf.assetFrontendBgcolorHex),
    searchPlaceholder,
    components,
    isLoading: q.isPending,
    recent,

    accountOpen,
    scanOpen,
    selectAssetOpen,
    firstTimeOpen,
    voiceComponent,

    /** Top actions, file components and video: on the home page every one of
     *  them first needs to know which asset the visitor means. */
    openSelectAsset: () => {
      closePanels()
      setSelectAssetOpen(true)
    },
    selectBottom: (c: PublicAssetFrontendApi$AssetComponentDto) => {
      if (isVoice(c)) {
        closePanels()
        setVoiceComponent(c)
      } else {
        setSelectAssetOpen(true)
      }
    },
    closeVoice: () => setVoiceComponent(null),
    openAccount: () => {
      closePanels()
      setAccountOpen(true)
    },
    closeAccount: () => setAccountOpen(false),
    openScan: () => {
      closePanels()
      setScanOpen(true)
    },
    closeScan: () => setScanOpen(false),
    closeSelectAsset: () => setSelectAssetOpen(false),
    openFirstTime: () => setFirstTimeOpen(true),
    closeFirstTime: () => {
      localStorage.setItem('firsttime_dialog_shown', 'true')
      setFirstTimeOpen(false)
    },
    goHome: () => {
      closePanels()
      navigate('/')
    },
  }
}
